import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Channel APIs
export const getChannels = async (params = {}) => {
  const response = await axios.get(`${API}/channels`, { params });
  return response.data;
};

export const getChannel = async (channelId) => {
  const response = await axios.get(`${API}/channels/${channelId}`);
  return response.data;
};

export const getStreamUrl = async (channelId, ua = 'browser') => {
  const response = await axios.get(`${API}/channels/${channelId}/stream`, { params: { ua } });
  return response.data;
};

export const confirmPlayback = async (channelId) => {
  try {
    await axios.post(`${API}/channels/${channelId}/heartbeat`);
  } catch (e) {
    // silent — don't crash the player if heartbeat fails
  }
};

// Categories
export const getCategories = async (includeAdult = false) => {
  const response = await axios.get(`${API}/categories`, { params: { include_adult: includeAdult } });
  return response.data;
};

// Favorites
export const getFavorites = async () => {
  const response = await axios.get(`${API}/favorites`);
  return response.data;
};

export const addFavorite = async (channelId) => {
  const response = await axios.post(`${API}/favorites`, { channel_id: channelId });
  return response.data;
};

export const removeFavorite = async (channelId) => {
  const response = await axios.delete(`${API}/favorites/${channelId}`);
  return response.data;
};

// Watch History
export const getHistory = async () => {
  const response = await axios.get(`${API}/history`);
  return response.data;
};

export const addToHistory = async (channelId, duration = 0) => {
  const response = await axios.post(`${API}/history`, { channel_id: channelId, duration });
  return response.data;
};

export const clearHistory = async () => {
  const response = await axios.delete(`${API}/history`);
  return response.data;
};

// Parental Controls
export const setParentalPin = async (pin, enabled) => {
  const response = await axios.post(`${API}/parental/pin`, { pin, enabled });
  return response.data;
};

export const verifyParentalPin = async (pin) => {
  const response = await axios.post(`${API}/parental/verify`, null, { params: { pin } });
  return response.data;
};

export const unlockAdultContent = async (pin) => {
  const response = await axios.post(`${API}/parental/unlock`, { pin });
  return response.data;
};

// EPG
export const getChannelEPG = async (channelId) => {
  const response = await axios.get(`${API}/epg/${channelId}`);
  return response.data;
};

export const getAllEPG = async () => {
  const response = await axios.get(`${API}/epg`);
  return response.data;
};

// Admin APIs
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`);
  return response.data;
};

export const getAdminUsers = async (skip = 0, limit = 50) => {
  const response = await axios.get(`${API}/admin/users`, { params: { skip, limit } });
  return response.data;
};

export const getAdminUser = async (userId) => {
  const response = await axios.get(`${API}/admin/users/${userId}`);
  return response.data;
};

export const updateAdminUser = async (userId, data) => {
  const response = await axios.patch(`${API}/admin/users/${userId}`, data);
  return response.data;
};

export const deleteAdminUser = async (userId) => {
  const response = await axios.delete(`${API}/admin/users/${userId}`);
  return response.data;
};

export const updateUserCredits = async (userId, credits, action) => {
  const response = await axios.post(`${API}/admin/credits`, { user_id: userId, credits, action });
  return response.data;
};

export const updateUserSubscription = async (userId, subscriptionType, days) => {
  const response = await axios.post(`${API}/admin/subscription`, { 
    user_id: userId, 
    subscription_type: subscriptionType,
    days
  });
  return response.data;
};

// Playlist Management
export const getPlaylists = async () => {
  const response = await axios.get(`${API}/admin/playlists`);
  return response.data;
};

export const createPlaylist = async (name, url) => {
  const response = await axios.post(`${API}/admin/playlists`, { name, url, is_active: true });
  return response.data;
};

export const syncPlaylist = async (playlistId) => {
  const response = await axios.post(`${API}/admin/playlists/${playlistId}/sync`);
  return response.data;
};

export const deletePlaylist = async (playlistId) => {
  const response = await axios.delete(`${API}/admin/playlists/${playlistId}`);
  return response.data;
};

export const getIPTVSources = async () => {
  const response = await axios.get(`${API}/admin/iptv-sources`);
  return response.data;
};

// Admin Create User
export const createAdminUser = async (userData) => {
  const response = await axios.post(`${API}/admin/users`, userData);
  return response.data;
};

// EPG Sync
export const syncEPG = async (epgUrl) => {
  const response = await axios.post(`${API}/admin/epg/sync`, null, { params: { epg_url: epgUrl } });
  return response.data;
};

// Scanner API
export const getScannerStatus = async () => {
  const response = await axios.get(`${API}/admin/scanner/status`);
  return response.data;
};

export const triggerScan = async () => {
  const response = await axios.post(`${API}/admin/scanner/trigger`);
  return response.data;
};

export const getScannerSources = async () => {
  const response = await axios.get(`${API}/admin/scanner/sources`);
  return response.data;
};

export const addCustomSource = async (name, url, contentType = 'live') => {
  const response = await axios.post(`${API}/admin/scanner/sources`, { name, url, content_type: contentType });
  return response.data;
};

export const removeCustomSource = async (sourceId) => {
  const response = await axios.delete(`${API}/admin/scanner/sources/${sourceId}`);
  return response.data;
};

export const getScannerLogs = async (limit = 20) => {
  const response = await axios.get(`${API}/admin/scanner/logs`, { params: { limit } });
  return response.data;
};

// Xtream Codes API
export const xtreamLogin = async (username, password) => {
  const response = await axios.post(`${API}/auth/xtream-login`, { username, password });
  return response.data;
};

export const importXtream = async (serverUrl, username, password, importLive = true, importVod = true) => {
  const response = await axios.post(`${API}/admin/xtream/import`, {
    server_url: serverUrl,
    username,
    password,
    import_live: importLive,
    import_vod: importVod,
  });
  return response.data;
};

export const getXtreamConnections = async () => {
  const response = await axios.get(`${API}/admin/xtream/connections`);
  return response.data;
};

export const deleteXtreamConnection = async (serverUrl) => {
  const response = await axios.delete(`${API}/admin/xtream/connections`, { params: { server_url: serverUrl } });
  return response.data;
};

export const updateXtreamConnection = async (serverUrl, username, password) => {
  const response = await axios.put(`${API}/admin/xtream/connections`, { server_url: serverUrl, username, password });
  return response.data;
};

// Series API
export const getSeriesCategories = async () => {
  const response = await axios.get(`${API}/series/categories`);
  return response.data;
};

export const getSeriesList = async (params = {}) => {
  const response = await axios.get(`${API}/series`, { params });
  return response.data;
};

export const getSeriesDetail = async (seriesId) => {
  const response = await axios.get(`${API}/series/${seriesId}`);
  return response.data;
};

// VOD (Movies) API
export const getVodCategories = async () => {
  const response = await axios.get(`${API}/vod/categories`);
  return response.data;
};

export const getVodList = async (params = {}) => {
  const response = await axios.get(`${API}/vod`, { params });
  return response.data;
};

// AI Chat API
export const sendAIMessage = async (message, sessionId = null) => {
  const response = await axios.post(`${API}/admin/ai/chat`, { 
    message, 
    session_id: sessionId 
  });
  return response.data;
};

export const getAIChatHistory = async (sessionId = null, limit = 20) => {
  const response = await axios.get(`${API}/admin/ai/history`, { 
    params: { session_id: sessionId, limit } 
  });
  return response.data;
};

export default {
  getChannels,
  getChannel,
  getStreamUrl,
  getCategories,
  getFavorites,
  addFavorite,
  removeFavorite,
  getHistory,
  addToHistory,
  clearHistory,
  setParentalPin,
  verifyParentalPin,
  unlockAdultContent,
  getChannelEPG,
  getAllEPG,
  getAdminStats,
  getAdminUsers,
  getAdminUser,
  updateAdminUser,
  deleteAdminUser,
  createAdminUser,
  updateUserCredits,
  updateUserSubscription,
  getPlaylists,
  createPlaylist,
  syncPlaylist,
  deletePlaylist,
  getIPTVSources,
  syncEPG,
  getScannerStatus,
  triggerScan,
  getScannerSources,
  addCustomSource,
  removeCustomSource,
  getScannerLogs,
  sendAIMessage,
  getAIChatHistory
};
